import type { SearchResult } from '../hooks/useSearch'

interface SearchResultCardProps {
  result: SearchResult
  query: string
  onClick: () => void
}

export function SearchResultCard({ result, query, onClick }: SearchResultCardProps) {
  // Highlight matching text
  const highlightMatch = (text: string | null, maxLength: number = 200): React.ReactNode => {
    if (!text) return null

    const truncated = text.length > maxLength
      ? text.slice(0, maxLength) + '...'
      : text

    if (!query.trim()) return truncated

    // Escape regex special characters in query
    const escapedQuery = query.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(`(${escapedQuery.split(/\s+/).join('|')})`, 'gi')
    const parts = truncated.split(regex)

    return parts.map((part, i) =>
      regex.test(part)
        ? <mark key={i} className="search-match">{part}</mark>
        : part
    )
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`

    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    })
  }

  const getSourceIcon = (sourceType: string | null) => {
    switch (sourceType) {
      case 'youtube':
        return '🎬'
      case 'pdf':
        return '📄'
      case 'newsletter':
        return '📧'
      default:
        return '📰'
    }
  }

  return (
    <li className="search-result-card" onClick={onClick}>
      <div className="result-icon">
        {getSourceIcon(result.source_type)}
      </div>

      <div className="result-content">
        <h3 className="result-title">
          {highlightMatch(result.title, 100)}
        </h3>

        <div className="result-meta">
          <span className="result-source">{result.source_type || 'article'}</span>
          <span className="result-date">{formatDate(result.created_at)}</span>
          {result.reading_time > 0 && (
            <span className="result-time">{result.reading_time} min read</span>
          )}
          {result.reading_progress > 0 && result.reading_progress < 100 && (
            <span className="result-progress">{Math.round(result.reading_progress)}% read</span>
          )}
        </div>

        {result.excerpt && (
          <p className="result-excerpt">
            {highlightMatch(result.excerpt, 150)}
          </p>
        )}

        <div className="result-badges">
          {result.is_read && <span className="badge read">Read</span>}
          {result.is_favorite && <span className="badge favorite">★ Favorite</span>}
        </div>
      </div>

      <div className="result-rank" title={`Relevance: ${(result.rank * 100).toFixed(0)}%`}>
        <div
          className="rank-bar"
          style={{ height: `${Math.min(result.rank * 100, 100)}%` }}
        />
      </div>
    </li>
  )
}
